import dayjs, { Dayjs } from "dayjs";

export const addOrdinalSuffixToDay = (date: Dayjs) => {
  const day = date.date();
  if (day > 3 && day < 21) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

export const formatMessageTime = (
  timestamp: number,
  format: string = "h:mm A"
): string => {
  return dayjs(timestamp).format(format);
};

export const formatDayHeader = (timestamp: number) => {
  return dayjs(timestamp).format("D MMM, YYYY");
};

export const normalizeTimestamp = (
  timestamp: number | Date | undefined
): number => {
  if (!timestamp) {
    console.error("Invalid timestamp:", timestamp);
    return 0;
  }
  if (timestamp instanceof Date) {
    return timestamp.getTime();
  }
  return Math.round(timestamp * 1000);
};

export const isNewDay = (
  currentTimestamp: number,
  previousTimestamp?: number
) => {
  const currentDate = dayjs.unix(currentTimestamp).startOf("day");
  const previousDate = previousTimestamp
    ? dayjs.unix(previousTimestamp).startOf("day")
    : null;

  return !previousDate || !currentDate.isSame(previousDate, "day");
};

export const formatDateSeparator = (timestamp: number) => {
  const date = dayjs.unix(timestamp);
  return `${date.format("MMMM")} ${addOrdinalSuffixToDay(date)}, ${date.year()}`;
};

// Round a time to the next interval (e.g., 15 minutes) and zero out seconds/ms.
export const roundToNextInterval = (
  date: Dayjs,
  intervalMinutes = 15
): Dayjs => {
  const remainder = date.minute() % intervalMinutes;
  if (remainder === 0) return date.second(0).millisecond(0);
  return date
    .add(intervalMinutes - remainder, "minute")
    .second(0)
    .millisecond(0);
};

// Compute default appointment date/time: start rounded up to next interval,
// end = start + 30 minutes. Returns date, startTime, endTime.
export const computeDefaultTimes = (
  intervalMinutes = 15,
  durationMinutes = 30
) => {
  const now = dayjs();
  const start = roundToNextInterval(now, intervalMinutes);
  const end = start.add(durationMinutes, "minute");
  return {
    date: start,
    startTime: start,
    endTime: end,
  };
};

// Format a calendar date (date of birth, release date) as YYYY-MM-DD for the API.
// A string is a value the API already gave us, so its date part is returned unchanged:
// `new Date("2000-01-01")` is midnight UTC, and reading it back in local time gives
// 1999-12-31 anywhere behind UTC, which moved the date back a day on every save (PRET-2082).
// Anything else comes from a date picker, which means local midnight, so it is read locally.
export const toCalendarDate = (date: string | number | Date): string => {
  if (typeof date === "string") {
    const calendarDate = /^\d{4}-\d{2}-\d{2}/.exec(date);
    if (calendarDate) return calendarDate[0];
  }
  const d = new Date(date);
  return (
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0")
  );
};
